import type {
  AssignmentPlan,
  LiveParticipantOption,
  LiveUnplacedRow,
  MatchedBy,
  RoomParticipant,
  UnplacedPlanOption,
} from "../types.js";
import { normalizeName } from "./name-normalize.js";

export interface ReverseMatchInput {
  plan: AssignmentPlan;
  /** Everyone Zoom reports in the meeting right now. */
  live: LiveParticipantOption[];
  /** Host/co-host UUIDs — they attend as operators, never as placeable attendees. */
  operatorUuids: Set<string>;
  /** UUIDs already in a breakout room (session claims plus plan-persisted ids). */
  placedUuids: Set<string>;
  /** Verified emails Zoom has surrendered, by live UUID. */
  emailsByUuid: Map<string, string>;
  /** Organiser-confirmed identities from earlier rounds, keyed by attendee key. */
  manualPlacements: Map<string, LiveParticipantOption>;
  attendeeKeyOf: (p: RoomParticipant) => string;
}

/** Plan participants with no Zoom identity yet — the pool a live attendee can be. */
export function unplacedPlanOptions(
  plan: AssignmentPlan,
  attendeeKeyOf: (p: RoomParticipant) => string,
): UnplacedPlanOption[] {
  const options: UnplacedPlanOption[] = [];
  for (const room of plan.rooms) {
    for (const p of room.participants) {
      if (p.zoom_participant_id) continue;
      options.push({
        attendeeKey: attendeeKeyOf(p),
        displayName: p.display_name,
        email: p.email,
        roomKey: room.room_key,
        roomName: room.room_name,
      });
    }
  }
  return options;
}

/** True when a live screen name matches one of a plan participant's confirmed past aliases. */
function aliasMatches(knownNames: string[] | undefined, screenName: string): boolean {
  if (!knownNames || knownNames.length === 0) return false;
  const screenKey = screenName.trim().toLowerCase();
  const screenNormalized = normalizeName(screenName);
  return knownNames.some((known) => {
    const knownKey = known.trim().toLowerCase();
    return knownKey === screenKey || normalizeName(known) === screenNormalized;
  });
}

/**
 * One row per live attendee who still needs a room, in the order Zoom reports
 * them.
 *
 * Only the tiers auto-matching already trusts produce a suggestion: a
 * remembered organiser decision, a verified email, an exact name, a
 * normalized name, or a saved alias. The partial and prefix tiers
 * (findRawLiveCandidate in main.ts) are deliberately excluded — they are
 * guesses the app only makes when it can prove uniqueness, and a pre-filled
 * guess an operator confirms without reading is worse than an empty field
 * (VE-023 AC-6).
 */
export function buildLiveUnplacedRows(input: ReverseMatchInput): LiveUnplacedRow[] {
  const pool = unplacedPlanOptions(input.plan, input.attendeeKeyOf);
  const byEmail = new Map(pool.map((o) => [o.email.trim().toLowerCase(), o]));
  const byName = new Map(pool.map((o) => [o.displayName.trim().toLowerCase(), o]));
  const byNormalized = new Map(
    pool.flatMap((o) => {
      const n = normalizeName(o.displayName);
      return n ? [[n, o] as const] : [];
    }),
  );
  const byKey = new Map(pool.map((o) => [o.attendeeKey, o]));
  // Reverse of manualPlacements: which attendee key a live UUID was confirmed as.
  const rememberedByUuid = new Map(
    Array.from(input.manualPlacements, ([key, live]) => [live.uuid, key] as const),
  );
  const knownNamesByKey = new Map(
    input.plan.rooms.flatMap((room) =>
      room.participants.map((p) => [input.attendeeKeyOf(p), p.known_names] as const),
    ),
  );

  function suggestFor(live: LiveParticipantOption): { option: UnplacedPlanOption; matchedBy: MatchedBy } | null {
    const rememberedKey = rememberedByUuid.get(live.uuid);
    const remembered = rememberedKey ? byKey.get(rememberedKey) : undefined;
    if (remembered) return { option: remembered, matchedBy: "manual" };

    const email = input.emailsByUuid.get(live.uuid);
    const byEmailHit = email ? byEmail.get(email.trim().toLowerCase()) : undefined;
    if (byEmailHit) return { option: byEmailHit, matchedBy: "email" };

    const nameHit = byName.get(live.screenName.trim().toLowerCase());
    if (nameHit) return { option: nameHit, matchedBy: "name" };

    const normalized = normalizeName(live.screenName);
    const normalizedHit = normalized ? byNormalized.get(normalized) : undefined;
    if (normalizedHit) return { option: normalizedHit, matchedBy: "normalized_name" };

    const aliasHit = pool.find((o) => aliasMatches(knownNamesByKey.get(o.attendeeKey), live.screenName));
    if (aliasHit) return { option: aliasHit, matchedBy: "alias" };

    return null;
  }

  const suggested = new Set<string>();
  const rows: LiveUnplacedRow[] = [];

  for (const live of input.live) {
    if (input.operatorUuids.has(live.uuid) || input.placedUuids.has(live.uuid)) continue;

    const match = suggestFor(live);
    // First row to want a participant claims them: a second live attendee with
    // the same display name falls through to manual search rather than both
    // rows pointing at one seat.
    const suggestion = match && !suggested.has(match.option.attendeeKey) ? match : null;
    if (suggestion) suggested.add(suggestion.option.attendeeKey);

    rows.push({
      live,
      suggestion: suggestion ? { ...suggestion.option, matchedBy: suggestion.matchedBy } : null,
    });
  }
  return rows;
}

/**
 * Plan participants nobody in the meeting matched — the CSV rows who have not
 * shown up. Derived from buildLiveUnplacedRows so it stays the exact complement
 * of the suggestions offered in the place dropdown: every attendee key some
 * live row claimed is present, everything left in the unplaced pool is absent.
 *
 * Already-placed participants are excluded for free — unplacedPlanOptions skips
 * anyone carrying a zoom_participant_id.
 */
export function absentPlanParticipants(input: ReverseMatchInput): UnplacedPlanOption[] {
  const matchedKeys = new Set(
    buildLiveUnplacedRows(input).flatMap((r) => (r.suggestion ? [r.suggestion.attendeeKey] : [])),
  );
  return unplacedPlanOptions(input.plan, input.attendeeKeyOf).filter(
    (o) => !matchedKeys.has(o.attendeeKey),
  );
}
